/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/tenant-admin/AnchorPicker.tsx
 * PURPOSE: Cascading L1→L8 organization picker for mandatory organization_id
 *          selection on tenant user create/edit forms (DD-0051 strict mode).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-02
 * COMPLIANCE: SDLC 6.5.0 + DD-0051 v0.3.1 D1 + BR-TENM-013 v0.2.1 REQ-I
 * PILLAR: P1 (Stage 04 - BUILD) + P4 (Quality Gates) + P6 (UI/UX)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance, Rule 9: Documentation
 *
 * Per BR-PADM-008 v1.5.1 REQ-G: organization_id mandatory cho non-super-admin.
 * Per DD-0051 v0.3.1 D1: anchor strict mode - empty/null rejected for tenant users.
 * Per BR-TENM-013 v0.2.1 REQ-I: cascading dropdown UX for 8-layer hierarchy.
 *
 * Issue #713 (BOT-024, P2, 2026-09-02): cascade dropdown option label reverted
 * from "tên · mã" (AP-2, 2026-07-30) back to "mã · tên" (ISS-PILOT-12 order),
 * matching the "ĐƠN VỊ" column on the same page. Independent of issue #699
 * (org tree table — closed BY DESIGN, keeps tên-first for chevron/indent
 * affordance); this cascade select has no such affordance and does not share
 * a formatting helper with the tree table.
 *
 * @business-context
 * - Domain: Multi-tenant org admin, Vietnamese 8-layer hierarchy
 * - Impact: Compliance (org-scope enforcement), data integrity (no orphan users)
 * - Stakeholders: Tenant admins, branch managers, platform compliance auditors
 *
 * @technical-architecture
 * - Layer: Presentation (controlled component)
 * - Pattern: Cascading single-select with hierarchical filtering
 * - Dependencies: React 18, TanStack Query (via useOrganizationTree), shadcn/ui Select
 *
 * @cross-references
 * - BR: docs/01-planning/01-requirements/03-tenant-management/BR-TENM-013-USER-ANCHOR-LIFECYCLE.md (REQ-I)
 * - DD: docs/02-design/18-adrs/06-decisions/DD-0051-tenant-user-org-anchor.md (D1)
 * - BR: docs/01-planning/01-requirements/02-platform-admin/BR-PADM-008-ORG-SCOPE-ENFORCEMENT.md (REQ-G)
 * - Issue: #713 (BOT-024) — dropdown mã-trước-tên fix; #699 — org tree table (unrelated, BY DESIGN)
 * - Hook: frontend/src/hooks/useOrganizationTree.ts
 * - Consumer: frontend/src/pages/tenant-admin/TenantUserManagement.tsx
 */

import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../../store/auth";
import {
  useOrganizationTree,
  type OrganizationNode,
  type OrgLevel,
} from "../../hooks/useOrganizationTree";
import { OrgCreateSidePanel } from "./OrgCreateSidePanel";
// AP-1b (2026-07-30): prompt cascade derive từ danh mục unit-types (BS-008)
// thay nhãn cấp cứng — tái sử dụng map + fetch của SC-006 (OrganizationModal)
import {
  fetchUnitTypes,
  UNIT_TYPE_ORG_MAP,
  type UnitType,
} from "../../pages/OrganizationManagement/components/OrganizationModal";

export interface AnchorPickerProps {
  /** Tenant scope; pass undefined for current-user tenant inferred from JWT. */
  tenantId?: string;
  /** Selected organization_id (UUID). Empty string = no selection. */
  value: string;
  /** Called with selected organization_id; empty string when cleared. */
  onChange: (organizationId: string) => void;
  /** Render as required (shows "*" + Vietnamese error message when empty). */
  required?: boolean;
  /** Disable all selects (e.g. during form submit). */
  disabled?: boolean;
  /** Optional label override. Default: "Đơn vị tổ chức". */
  label?: string;
  /**
   * Per DD-0052 v0.1.0 §5.1: when true, renders inline "+ Tạo đơn vị tổ chức mới"
   * footer button that opens OrgCreateSidePanel without unmounting the parent
   * form (preserves form state per AC-UX-2). Defaults to false to keep existing
   * call sites unchanged.
   */
  enableInlineCreate?: boolean;
}

/**
 * Nhãn cấp FALLBACK theo TIẾNG VIỆT (AP-1, 2026-07-30): D10 cấm thuật ngữ
 * English trên UI. Chỉ dùng khi danh mục unit-types chưa tải/lỗi (AP-1b) —
 * nhãn chuẩn lấy từ danh mục /tenant-admin/unit-types (xem levelLabelByLevel).
 * Giữ đủ 8 cấp vì data model backend vẫn 8 lớp (Note A @architect).
 */
const LEVEL_LABELS: Record<OrgLevel, string> = {
  1: "Tập đoàn",
  2: "Chuỗi",
  3: "Thương hiệu",
  4: "Vùng",
  5: "Chi nhánh",
  6: "Khu vực làm việc",
  7: "Ca làm việc",
  8: "Đội nhóm",
};

/**
 * Cascading dropdown picker. Each level shows only nodes whose parent matches
 * the selection at the previous level. Selecting a node at any level commits
 * its UUID via onChange (deepest non-empty selection wins).
 */
export const AnchorPicker: React.FC<AnchorPickerProps> = ({
  tenantId,
  value,
  onChange,
  required = false,
  disabled = false,
  label = "Đơn vị tổ chức",
  enableInlineCreate = false,
}) => {
  const { t } = useTranslation();
  const { flat, isLoading, isError } = useOrganizationTree(tenantId);

  // DD-0052 §5.3: inline create gated by capability. TENANT_ADMIN/TENANT_OWNER
  // permission "manage_organization" maps to TENANT_USER_MANAGE/TENANT_SETTINGS
  // capability codes for these roles. Fail-closed when capabilities array is
  // empty (defensive — JWT mismatch).
  const { capabilities, userRole } = useAuthStore();
  const canCreateOrg =
    enableInlineCreate &&
    (userRole === "TENANT_ADMIN" || userRole === "TENANT_OWNER") &&
    // Reuse existing TENANT_SETTINGS capability code (org tree management is
    // part of tenant settings family per BR-TENM-013 v0.2.2 §12.5). Backend
    // enforces fine-grained "manage_organization" permission on the actual
    // POST /api/v1/organizations endpoint regardless of frontend gate.
    (capabilities.includes("TENANT_SETTINGS") ||
      capabilities.includes("TENANT_USER_MANAGE") ||
      capabilities.length === 0); // TA-010 fallback per existing codebase pattern

  const [panelOpen, setPanelOpen] = useState(false);

  // ISSUE-028 fix: search state — SPEC-0417 v1.3 anchor_picker.search.* i18n
  const [searchQuery, setSearchQuery] = useState("");

  // AP-1b (2026-07-30): danh mục loại đơn vị cho nhãn prompt cascade —
  // "Công ty vận hành"/"Cơ sở" theo registry thay nhãn cấp cứng legacy.
  // Fallback LEVEL_LABELS (VI) khi API lỗi — zero-mock, không hardcode danh mục giả.
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchUnitTypes()
      .then((list) => { if (!cancelled) setUnitTypes(list); })
      .catch(() => { /* giữ fallback LEVEL_LABELS */ });
    return () => { cancelled = true; };
  }, []);

  /** level (1-8) → tên loại từ danh mục unit-types; fallback nhãn legacy VI. */
  const levelLabelByLevel = useMemo(() => {
    const map = new Map<number, string>();
    unitTypes.forEach((t) => {
      const lvl = UNIT_TYPE_ORG_MAP[t.code]?.level;
      if (lvl != null && t.is_active) map.set(lvl, t.name);
    });
    return map;
  }, [unitTypes]);

  const levelLabel = (levelNum: number): string =>
    levelLabelByLevel.get(levelNum) ?? LEVEL_LABELS[levelNum as OrgLevel] ?? `Cấp ${levelNum}`;

  /** Flat id→node map for O(1) ancestor lookup. */
  const byId = useMemo(
    () => new Map(flat.map((n) => [n.id, n])),
    [flat]
  );

  /** Build "L1 › L2 › … › node" breadcrumb for a given node id. */
  const getBreadcrumb = (nodeId: string): string => {
    const parts: string[] = [];
    let cursor: OrganizationNode | undefined = byId.get(nodeId);
    while (cursor) {
      parts.unshift(cursor.name_vietnamese || cursor.name);
      cursor = cursor.parent_id ? byId.get(cursor.parent_id) : undefined;
    }
    return parts.join(" › ");
  };

  /** Nodes matching current search query (max 20). Empty when query is blank. */
  const filteredNodes = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return flat
      .filter(
        (n) =>
          (n.name_vietnamese || n.name).toLowerCase().includes(q) ||
          n.code.toLowerCase().includes(q)
      )
      .slice(0, 20);
  }, [searchQuery, flat]);

  /**
   * Compute the chain of ancestors for the currently-selected value, so we
   * can pre-populate each cascade level select. Walks parent_id upwards.
   */
  const selectionChain = useMemo<string[]>(() => {
    if (!value || flat.length === 0) return [];
    const byId = new Map(flat.map((n) => [n.id, n]));
    const chain: string[] = [];
    let cursor: OrganizationNode | undefined = byId.get(value);
    while (cursor) {
      chain.unshift(cursor.id);
      cursor = cursor.parent_id ? byId.get(cursor.parent_id) : undefined;
    }
    return chain;
  }, [value, flat]);

  /** Group nodes by parent_id for fast cascade lookup. */
  const childrenByParent = useMemo(() => {
    const map = new Map<string | "ROOT", OrganizationNode[]>();
    flat.forEach((n) => {
      const key = n.parent_id ?? "ROOT";
      const arr = map.get(key) ?? [];
      arr.push(n);
      map.set(key, arr);
    });
    map.forEach((arr) =>
      arr.sort((a, b) => a.name.localeCompare(b.name, "vi"))
    );
    return map;
  }, [flat]);

  // DD-0052 §5.1: parent for inline-create side-panel = currently-selected node
  // (deepest selection wins). Falls back to undefined → panel creates L1 root.
  // NOTE (ISS-0014): hook MUST stay above the early returns below — a useMemo
  // after a conditional return causes React #300 (fewer hooks than expected).
  const currentParent = useMemo<OrganizationNode | null>(() => {
    if (!value) return null;
    const found = flat.find((n) => n.id === value);
    return found ?? null;
  }, [value, flat]);

  /**
   * Handle change at a specific cascade level. Truncates selection chain at
   * that level — selecting a parent invalidates deeper selections.
   */
  const handleLevelChange = (levelIndex: number, newId: string) => {
    if (!newId) {
      // Cleared at this level — fall back to parent (level - 1) or empty.
      const parentId = levelIndex > 0 ? selectionChain[levelIndex - 1] : "";
      onChange(parentId);
      return;
    }
    onChange(newId);
  };

  // Empty-state: no orgs at all in this tenant.
  if (!isLoading && !isError && flat.length === 0) {
    return (
      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
        <div
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
          role="alert"
        >
          {t("tenant_admin.org_tree.anchor_picker.empty_state")}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
        <div
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
          role="alert"
        >
          {t("tenant_admin.org_tree.anchor_picker.load_error")}
        </div>
      </div>
    );
  }

  // Build cascade levels: start from roots (parent=ROOT), then children of
  // each selected ancestor in the chain. Stop after the deepest selected node
  // OR when no children exist.
  const levels: { parentId: string | "ROOT"; selectedId: string }[] = [
    { parentId: "ROOT", selectedId: selectionChain[0] ?? "" },
  ];
  for (let i = 0; i < selectionChain.length; i++) {
    const selectedAtThisLevel = selectionChain[i];
    const children = childrenByParent.get(selectedAtThisLevel) ?? [];
    if (children.length > 0) {
      levels.push({
        parentId: selectedAtThisLevel,
        selectedId: selectionChain[i + 1] ?? "",
      });
    }
  }

  const showRequiredError = required && !value && !isLoading;

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>

      {/* ISSUE-028 fix: search input — SPEC-0417 v1.3 anchor_picker.search.placeholder */}
      {!isLoading && flat.length > 0 && (
        <div className="relative">
          <input
            type="text"
            data-testid="anchor-picker-search"
            className="flex h-9 w-full rounded-md border border-gray-300 bg-white px-3 py-1 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder={t("tenant_admin.org_tree.anchor_picker.search.placeholder", "Tìm kiếm đơn vị...")}
            value={searchQuery}
            disabled={disabled}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label={t("tenant_admin.org_tree.anchor_picker.search.placeholder", "Tìm kiếm đơn vị...")}
          />
          {searchQuery && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setSearchQuery("")}
              aria-label={t("tenant_admin.org_tree.anchor_picker.action.clear", "Xóa chọn")}
            >
              ✕
            </button>
          )}
        </div>
      )}

      {/* Flat search results — shown when query is non-empty */}
      {searchQuery.trim() && (
        <div
          data-testid="anchor-picker-search-results"
          className="max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-sm"
        >
          {filteredNodes.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-500">
              {t("tenant_admin.org_tree.anchor_picker.search.empty", "Không tìm thấy đơn vị")}
            </p>
          ) : (
            filteredNodes.map((node) => (
              <button
                key={node.id}
                type="button"
                data-testid={`anchor-picker-result-${node.id}`}
                className="flex w-full flex-col px-3 py-2 text-left hover:bg-orange-50 focus:bg-orange-50 focus:outline-none"
                onClick={() => {
                  onChange(node.id);
                  setSearchQuery("");
                }}
              >
                <span className="text-sm font-medium text-gray-900">
                  {node.name_vietnamese || node.name}
                </span>
                <span className="text-xs text-gray-500">{getBreadcrumb(node.id)}</span>
              </button>
            ))
          )}
        </div>
      )}

      {/* Cascade dropdowns — shown when search is empty (existing behavior unchanged) */}
      <div className={searchQuery.trim() ? "hidden" : "space-y-2"}>
        {levels.map((lvl, idx) => {
          const options = childrenByParent.get(lvl.parentId) ?? [];
          if (options.length === 0) return null;
          // Detect level number: MIN level trong nhóm (không phải option đầu tiên) —
          // root có thể trộn cấp (vd Tập đoàn L1 + Cơ sở L2 cùng parent=null),
          // lấy option[0] khiến nhãn prompt "đứng im" sai loại (2026-08-05).
          const levelNum = (options.length
            ? Math.min(...options.map((o) => o.level ?? 8))
            : idx + 1) as OrgLevel;
          // AP-1b: nhãn prompt từ danh mục unit-types (fallback legacy VI)
          const promptLabel = `-- Chọn ${levelLabel(levelNum)} --`;

          return (
            <select
              key={`${lvl.parentId}-${idx}`}
              className="flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:cursor-not-allowed disabled:opacity-50"
              value={lvl.selectedId}
              disabled={disabled || isLoading}
              onChange={(e) => handleLevelChange(idx, e.target.value)}
              aria-label={`${label} - ${levelLabel(levelNum)}`}
            >
              <option value="">{promptLabel}</option>
              {options.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {/* Issue #713 (BOT-024, P2): option hiển thị "mã · tên" theo quy ước
                      ISS-PILOT-12, khớp cột "ĐƠN VỊ" cùng trang. AP-2 (2026-07-30) đặt
                      "tên · mã" (ngược thứ tự) — KHÔNG cùng gốc với issue #699 (bảng cây
                      tổ chức, đóng BY DESIGN vì Tên cần đứng trước để mang chevron/thụt-lề):
                      dropdown filter này không có affordance nào cần giữ Tên trước. */}
                  {opt.code} · {opt.name_vietnamese || opt.name}
                </option>
              ))}
            </select>
          );
        })}
      </div>

      {isLoading && (
        <p className="text-xs text-gray-500">
          {t("tenant_admin.org_tree.anchor_picker.loading")}
        </p>
      )}

      {showRequiredError && (
        <p className="text-xs text-red-600" role="alert">
          {t("tenant_admin.org_tree.anchor_picker.required_error")}
        </p>
      )}

      {/* DD-0052 AC-UX-2: inline create-org footer button.
          Visible only when:
            - enableInlineCreate prop is true (parent opt-in)
            - User has manage_organization-equivalent capability (canCreateOrg)
            - Picker not in disabled state
          Clicking opens OrgCreateSidePanel as overlay sibling — does NOT
          unmount the parent Create User form (form state preservation). */}
      {canCreateOrg && !disabled && (
        <button
          type="button"
          onClick={() => setPanelOpen(true)}
          title={t("tenant_admin.org_tree.anchor_picker_inline_create_tooltip")}
          aria-label={t(
            "tenant_admin.org_tree.anchor_picker_inline_create_button"
          )}
          data-testid="anchor-picker-inline-create-button"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            marginTop: "4px",
            padding: "8px 12px",
            background: "transparent",
            border: "1px dashed var(--nqh-coffee-500, #d97706)",
            borderRadius: "6px",
            color: "var(--nqh-coffee-500, #d97706)",
            fontSize: "13px",
            fontWeight: 500,
            cursor: "pointer",
            minHeight: "44px",
          }}
        >
          {t("tenant_admin.org_tree.anchor_picker_inline_create_button")}
        </button>
      )}

      {/* Side-panel overlay (DD-0052 Option B side-panel slide). */}
      {canCreateOrg && (
        <OrgCreateSidePanel
          open={panelOpen}
          parent={currentParent}
          tenantId={tenantId}
          onClose={() => setPanelOpen(false)}
          onCreated={(newOrgId) => {
            setPanelOpen(false);
            // Auto-select the newly created org per AC-UX-2.
            onChange(newOrgId);
          }}
        />
      )}
    </div>
  );
};

export default AnchorPicker;
